// client/pages/ErrorPage.jsx
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext.jsx";
import AppButton from "../components/ui/AppButton.jsx";
import { ShieldOff, Search, WifiOff, ArrowLeft, Home, Zap, RefreshCw, LogIn, Clock } from "lucide-react";

/* ===== Error-Konfiguration ===== */
const ERROR_CONFIG = {
  403: {
    icon: ShieldOff,
    title: "Zugriff verweigert",
    subtitle: "Du hast keine Berechtigung für diese Seite.",
    description: "Falls du denkst, dass das ein Fehler ist, wende dich an deinen Administrator.",
    color: "red"
  },
  404: {
    icon: Search,
    title: "Seite nicht gefunden",
    subtitle: "Die angeforderte Seite existiert nicht.",
    description: "Vielleicht wurde sie verschoben, gelöscht oder du hast dich vertippt.",
    color: "amber"
  },
  408: {
    icon: Clock,
    title: "Sitzung abgelaufen",
    subtitle: "Deine Sitzung ist abgelaufen.",
    description: "Bitte melde dich erneut an, um fortzufahren.",
    color: "blue"
  },
  offline: {
    icon: WifiOff,
    title: "Keine Verbindung",
    subtitle: "Der Server ist nicht erreichbar.",
    description: "Bitte prüfe deine Internetverbindung und versuche es erneut.",
    color: "slate"
  }
};
const COLOR_MAP = {
  red: {
    iconBg: "bg-red-500/10 border-red-500/20",
    iconText: "text-red-400",
    accent: "red"
  },
  amber: {
    iconBg: "bg-amber-500/10 border-amber-500/20",
    iconText: "text-amber-400",
    accent: "amber"
  },
  blue: {
    iconBg: "bg-blue-500/10 border-blue-500/20",
    iconText: "text-blue-400",
    accent: "blue"
  },
  slate: {
    iconBg: "bg-slate-500/10 border-slate-500/20",
    iconText: "text-slate-400",
    accent: "slate"
  }
};
const LIGHT_COLOR_MAP = {
  red: {
    iconBg: "bg-red-50 border-red-200/50",
    iconText: "text-red-500",
    accent: "red"
  },
  amber: {
    iconBg: "bg-amber-50 border-amber-200/50",
    iconText: "text-amber-500",
    accent: "amber"
  },
  blue: {
    iconBg: "bg-blue-50 border-blue-200/50",
    iconText: "text-blue-500",
    accent: "blue"
  },
  slate: {
    iconBg: "bg-slate-100 border-slate-200/50",
    iconText: "text-slate-500",
    accent: "slate"
  }
};

/**
 * Premium Error Page
 */
export default function ErrorPage({
  code = 404,
  title,
  subtitle
}) {
    const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  const cardRef = useRef(null);
  const mountRafRef = useRef(null);
  const cfg = ERROR_CONFIG[code] || ERROR_CONFIG[404];
  const colors = COLOR_MAP[cfg.color];
  const Icon = cfg.icon;
  const displayCode = typeof code === "number" ? code : null;
  const wrtkLogo = "/assets/wrtk-glow.png";
  const jaeggiLogo = "/assets/jaeggi-white.png";
  useEffect(() => {
    document.title = `${displayCode || "Fehler"} • WireTrack`;
    mountRafRef.current = requestAnimationFrame(() => setMounted(true));
    return () => {
      if (mountRafRef.current) cancelAnimationFrame(mountRafRef.current);
    };
  }, [displayCode]);

  /* Subtle card tilt on mouse move */
  const handleMouseMove = useCallback(e => {
    const card = cardRef.current;
    if (!card || window.innerWidth < 768) return;
    const rect = card.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    card.style.transform = `perspective(800px) rotateY(${x * 5}deg) rotateX(${y * -5}deg)`;
  }, []);
  const handleMouseLeave = useCallback(() => {
    const card = cardRef.current;
    if (card) card.style.transform = "perspective(800px) rotateY(0deg) rotateX(0deg)";
  }, []);
  const goBack = () => {
    if (window.history.length > 2) navigate(-1);else navigate("/");
  };
  const goHome = () => navigate("/");
  const goLogin = () => navigate("/login");
  return <div className={`error-page min-h-screen w-full relative overflow-hidden ${"bg-gradient-to-br from-[#030508] via-[#070d1a] to-[#0a1a32] text-white"}`}>
      <ErrorBackground isDark={true} color={cfg.color} />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-8">
        <div className={`w-full max-w-lg transition-all duration-700 ease-out ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>

          {/* Logos above card – same layout as Login */}
          <div className={`mb-8 space-y-3 transition-all duration-700 delay-100 ${mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center justify-items-center gap-5 sm:gap-8">
              <div className="h-10 sm:h-12 flex items-center justify-end">
                <img src={wrtkLogo} alt="WireTrack" className={`max-h-full w-auto scale-[1.35] ${"neon-logo"}`} />
              </div>
              <div className="h-10 sm:h-12 flex items-center justify-center">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${"bg-blue-500/10 border border-blue-500/20"}`}>
                  <Zap className={`w-4.5 h-4.5 ${"text-blue-400 drop-shadow-[0_0_6px_rgba(59,130,246,0.8)]"}`} />
                </div>
              </div>
              <div className="h-10 sm:h-12 flex items-center justify-start">
                <img src={jaeggiLogo} alt="Jaeggi" className={`max-h-full w-auto scale-[0.85] ${"neon-logo"}`} style={{}} />
              </div>
            </div>
            <p className={`text-center text-[12px] tracking-wide ${"text-white/20"}`}>
              Elektro Management Platform
            </p>
          </div>

          {/* Main card */}
          <div ref={cardRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} className={`relative rounded-[28px] border overflow-hidden transition-[box-shadow,background] duration-300 ${"bg-white/[0.03] border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]"}`} style={{
          backdropFilter: "blur(24px)"
        }}>
            {isDark && <div className="login-glow absolute -inset-px rounded-[28px] pointer-events-none" aria-hidden="true" />}

            {/* Giant error code background */}
            {displayCode && <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none select-none" aria-hidden="true">
                <span className={`error-glitch text-[180px] sm:text-[220px] font-black leading-none tracking-tighter ${"text-white/[0.03]"}`} data-text={displayCode}>
                  {displayCode}
                </span>
              </div>}

            {/* Content */}
            <div className="relative z-10 p-8 sm:p-12 text-center space-y-6">
              {/* Icon with pulse ring */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className={`error-icon-pulse absolute -inset-3 rounded-[20px] ${"bg-current opacity-[0.04]"}`} style={{
                  color: cfg.color === "red" ? "#ef4444" : cfg.color === "amber" ? "#f59e0b" : cfg.color === "blue" ? "#3b82f6" : "#64748b"
                }} />
                  <div className={`relative inline-flex items-center justify-center w-16 h-16 rounded-2xl border ${colors.iconBg}`}>
                    <Icon className={`w-7 h-7 ${colors.iconText}`} />
                  </div>
                </div>
              </div>

              {/* Text */}
              <div className="space-y-2.5">
                <h1 className={`text-2xl sm:text-3xl font-bold tracking-tight ${"text-white"}`}>
                  {title || cfg.title}
                </h1>
                <p className={`text-[15px] leading-relaxed ${"text-white/50"}`}>
                  {subtitle || cfg.subtitle}
                </p>
              </div>

              <p className={`text-[13px] max-w-xs mx-auto leading-relaxed ${"text-white/25"}`}>
                {cfg.description}
              </p>

              {/* Path hint for 404 */}
              {code === 404 && location.pathname !== "/" && <div className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-mono ${"bg-white/[0.03] text-white/25 border border-white/[0.06]"}`}>
                  <span className={`text-[10px] uppercase tracking-wider font-sans font-semibold ${"text-white/15"}`}>Pfad</span>
                  <span className={`w-px h-3 ${"bg-white/10"}`} />
                  <span className="truncate max-w-[220px]">{location.pathname}</span>
                </div>}

              {/* Separator */}
              <div className={`h-px mx-auto max-w-[200px] ${"bg-gradient-to-r from-transparent via-white/[0.06] to-transparent"}`} />

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
                {code === 408 ? <ActionButton primary isDark={true} onClick={goLogin} icon={LogIn} label="Zum Login" /> : <>
                    <ActionButton primary isDark={true} onClick={goBack} icon={ArrowLeft} label="Zurück" />
                    <ActionButton isDark={true} onClick={goHome} icon={Home} label="Dashboard" />
                  </>}
                {code === "offline" && <ActionButton isDark={true} onClick={() => window.location.reload()} icon={RefreshCw} label="Neu laden" />}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={`flex items-center justify-center gap-2 mt-8 transition-all duration-700 delay-200 ${mounted ? "opacity-100" : "opacity-0"} ${"text-white/[0.12]"}`}>
            <Zap className="w-3 h-3" />
            <span className="text-[11px] tracking-wide font-medium">Verschlüsselte Verbindung</span>
            <span className="text-[11px]">·</span>
            <span className="text-[11px] tabular-nums font-medium">v1.11.2</span>
          </div>
        </div>
      </div>
    </div>;
}

/* ===== Named Exports ===== */
export function ForbiddenPage(props) {
  return <ErrorPage code={403} {...props} />;
}
export function NotFoundPage(props) {
  return <ErrorPage code={404} {...props} />;
}
export function SessionExpiredPage(props) {
  return <ErrorPage code={408} {...props} />;
}

/* ===== Action Button ===== */
function ActionButton({
  primary,
  isDark,
  onClick,
  icon: Icon,
  label
}) {
  return <AppButton type="button" variant={primary ? "primary" : "secondary"} size="lg" onClick={onClick} className={`group relative flex items-center gap-2.5 px-7 py-3.5 rounded-2xl text-sm font-semibold transition-all min-w-[140px] justify-center overflow-hidden ${primary ? "bg-gradient-to-r from-blue-600 via-blue-600 to-indigo-600 text-white shadow-[0_4px_16px_rgba(59,130,246,0.25)] hover:shadow-[0_8px_30px_rgba(59,130,246,0.4)] hover:-translate-y-0.5 active:translate-y-0" : "bg-white/[0.05] text-white/50 hover:bg-white/[0.1] hover:text-white/70 border border-white/[0.06] hover:border-white/[0.12]"}`}>
      {primary && <div className="login-btn-shimmer absolute inset-0 pointer-events-none" />}
      <Icon className="relative z-10 w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
      <span className="relative z-10">{label}</span>
    </AppButton>;
}

/* ===== Animated Background ===== */
function ErrorBackground({
  isDark,
  color
}) {
  const orbColor = {
    red: "bg-red-600/[0.05]",
    amber: "bg-amber-500/[0.04]",
    blue: "bg-blue-600/[0.06]",
    slate: "bg-slate-500/[0.04]"
  };
  return <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div className={`login-orb absolute w-[600px] h-[600px] rounded-full blur-[120px] ${orbColor[color] || orbColor.blue}`} style={{
      top: "-15%",
      right: "-10%",
      animationDelay: "0s"
    }} />
      <div className={`login-orb absolute w-[400px] h-[400px] rounded-full blur-[100px] ${"bg-indigo-500/[0.03]"}`} style={{
      bottom: "-15%",
      left: "-5%",
      animationDelay: "-7s"
    }} />
      <div className={`login-orb absolute w-[250px] h-[250px] rounded-full blur-[80px] ${orbColor[color] || orbColor.blue}`} style={{
      top: "50%",
      left: "20%",
      animationDelay: "-12s",
      opacity: 0.5
    }} />

      {/* Grid */}
      {isDark && <div className="absolute inset-0 opacity-[0.015]" style={{
      backgroundImage: "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
      backgroundSize: "60px 60px"
    }} />}

      {/* Sparks */}
      {isDark && Array.from({
      length: 8
    }).map((_, i) => <div key={i} className="spark absolute" style={{
      left: `${i * 71 % 100}%`,
      bottom: `${i * 23 % 60}px`,
      animationDelay: `${i * 0.35 % 2.5}s`
    }} />)}
    </div>;
}